import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  LayoutDashboard, ClipboardList, Target, Zap, Rocket, 
  CheckCircle, Lock, ChevronRight, Award, LogOut, TrendingUp,
  Calendar, Settings, BarChart3, ArrowLeft, Sparkles, RefreshCw, Loader2
} from "lucide-react";

const RAPID_STEPS = [
  { id: "intake", label: "Intake", status: "intake_pending" },
  { id: "session_1", label: "Session 1", status: "intake_complete" },
  { id: "session_2", label: "Session 2", status: "session_1" },
  { id: "session_3", label: "Session 3", status: "session_2" },
  { id: "momentum", label: "Momentum", status: "session_3" },
  { id: "completed", label: "Complete", status: "momentum" }
];

export const RapidDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        setEnrollment(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has access to R.A.P.I.D.™ (needs premium_rapid tier)
  const hasRapidAccess = () => {
    // If no tier set, allow access (legacy users) - to be assigned later by consultant
    if (!user?.tier) return true;
    // Only premium_rapid tier has access
    if (user.tier === "premium_rapid") return true;
    return false;
  };

  if (!hasRapidAccess()) {
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="rapid-locked">
        {/* Simple Header */}
        <div className="bg-white border-b border-neutral-200">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">MB</span>
                </div>
                <span className="font-semibold text-neutral-900">My Biz Consulting</span>
              </div>
              <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>

        {/* Locked Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-amber-600" />
                </div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-2">R.A.P.I.D.™ Business Recalibration</h1>
                <p className="text-lg text-neutral-600">Premium Program - Upgrade Required</p>
              </div>

              {/* Program Overview */}
              <div className="bg-white rounded-xl p-6 mb-6 border border-neutral-200">
                <h2 className="text-xl font-semibold text-neutral-900 mb-4">What You'll Get</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { icon: ClipboardList, title: "Strategic Intake", desc: "Deep-dive assessment of your business" },
                    { icon: Target, title: "3 Power Sessions", desc: "Review, Analyze, Plan, Implement & Develop" },
                    { icon: Rocket, title: "Momentum Plan", desc: "30-60-90 day implementation roadmap" },
                    { icon: Award, title: "Completion Certificate", desc: "Recognition of your transformation" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-3 bg-neutral-50 rounded-lg">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">{item.title}</p>
                        <p className="text-sm text-neutral-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <p className="text-neutral-600 mb-4">
                  Ready to transform your business with our proprietary R.A.P.I.D.™ framework?
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => window.open("mailto:support@mybizconsulting.com?subject=R.A.P.I.D.%20Upgrade%20Request", "_blank")}
                    data-testid="rapid-upgrade-btn"
                  >
                    Request Upgrade
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    data-testid="rapid-back-btn"
                  >
                    Back to Dashboard
                  </Button>
                </div>
                <p className="text-sm text-neutral-500 mt-4">
                  Contact your consultant to upgrade to the R.A.P.I.D.™ Premium tier
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Current Tier Info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Badge className="bg-blue-100 text-blue-700">Your Current Tier</Badge>
              <span className="text-blue-800 font-medium">
                {user?.tier === "basic" ? "Basic Consulting" : 
                 user?.tier === "growth_strategic" ? "Strategic Foundation" :
                 user?.tier === "growth_optimization" ? "Growth & Optimization" :
                 user?.tier === "growth_scale" ? "Scale & Leadership" :
                 user?.tier?.replace("_", " ") || "Basic"}
              </span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              You have access to Project Management, Weekly Tracking, and AI Tools. Upgrade to R.A.P.I.D.™ for our proprietary business transformation framework.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getStepStatus = (stepId) => {
    if (!enrollment) return "locked";
    const status = enrollment.status;
    
    const statusOrder = ["intake_pending", "intake_complete", "session_1", "session_2", "session_3", "momentum", "completed"];
    const currentIndex = statusOrder.indexOf(status);
    
    if (stepId === "intake") {
      return enrollment.intake_data ? "completed" : "current";
    }
    if (stepId === "session_1") {
      if (enrollment.session_1?.completed) return "completed";
      if (currentIndex >= 1) return enrollment.session_1?.pre_session_response ? "in_progress" : "current";
      return "locked";
    }
    if (stepId === "session_2") {
      if (enrollment.session_2?.completed) return "completed";
      if (currentIndex >= 3) return enrollment.session_2?.pre_session_response ? "in_progress" : "current";
      return "locked";
    }
    if (stepId === "session_3") {
      if (enrollment.session_3?.completed) return "completed";
      if (currentIndex >= 4) return enrollment.session_3?.pre_session_response ? "in_progress" : "current";
      return "locked";
    }
    if (stepId === "momentum") {
      if (status === "completed") return "completed";
      if (currentIndex >= 5) return "current";
      return "locked";
    }
    if (stepId === "completed") {
      return status === "completed" ? "completed" : "locked";
    }
    return "locked";
  };

  const getProgressPercentage = () => {
    if (!enrollment) return 0;
    const status = enrollment.status;
    const progressMap = {
      "intake_pending": 0,
      "intake_complete": 16,
      "session_1": 33,
      "session_2": 50,
      "session_3": 66,
      "momentum": 83,
      "completed": 100
    };
    return progressMap[status] || 0;
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST",  });
      logout();
      navigate("/");
    } catch (_error) {
      console.error("Logout _error:", _error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-emerald-700 text-white p-6 hidden lg:block">
        <div className="flex items-center space-x-2 mb-8">
          <div className="w-8 h-8 bg-white/10 rounded-md flex items-center justify-center">
            <span className="font-bold text-sm">MB</span>
          </div>
          <span className="font-semibold">My Biz Consulting</span>
        </div>

        <nav className="space-y-1">
          <Link to="/rapid" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white" data-testid="nav-rapid-home">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/rapid/intake" className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors ${getStepStatus("intake") === "locked" ? "opacity-50 pointer-events-none" : ""}`} data-testid="nav-intake">
            <ClipboardList className="w-5 h-5" />
            <span>My Intake</span>
            {getStepStatus("intake") === "completed" && <CheckCircle className="w-4 h-4 ml-auto text-emerald-300" />}
          </Link>
          <Link to="/rapid/session/1" className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors ${getStepStatus("session_1") === "locked" ? "opacity-50 pointer-events-none" : ""}`} data-testid="nav-session-1">
            <Target className="w-5 h-5" />
            <span>Session 1: Review</span>
            {getStepStatus("session_1") === "locked" && <Lock className="w-4 h-4 ml-auto" />}
            {getStepStatus("session_1") === "completed" && <CheckCircle className="w-4 h-4 ml-auto text-emerald-300" />}
          </Link>
          <Link to="/rapid/session/2" className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors ${getStepStatus("session_2") === "locked" ? "opacity-50 pointer-events-none" : ""}`} data-testid="nav-session-2">
            <Zap className="w-5 h-5" />
            <span>Session 2: Plan</span>
            {getStepStatus("session_2") === "locked" && <Lock className="w-4 h-4 ml-auto" />}
            {getStepStatus("session_2") === "completed" && <CheckCircle className="w-4 h-4 ml-auto text-emerald-300" />}
          </Link>
          <Link to="/rapid/session/3" className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors ${getStepStatus("session_3") === "locked" ? "opacity-50 pointer-events-none" : ""}`} data-testid="nav-session-3">
            <Rocket className="w-5 h-5" />
            <span>Session 3: Develop</span>
            {getStepStatus("session_3") === "locked" && <Lock className="w-4 h-4 ml-auto" />}
            {getStepStatus("session_3") === "completed" && <CheckCircle className="w-4 h-4 ml-auto text-emerald-300" />}
          </Link>
          <Link to="/rapid/momentum" className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors ${getStepStatus("momentum") === "locked" ? "opacity-50 pointer-events-none" : ""}`} data-testid="nav-momentum">
            <TrendingUp className="w-5 h-5" />
            <span>Momentum Plan</span>
            {getStepStatus("momentum") === "locked" && <Lock className="w-4 h-4 ml-auto" />}
          </Link>
          <Link to="/rapid/completion" className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors ${getStepStatus("completed") === "locked" ? "opacity-50 pointer-events-none" : ""}`} data-testid="nav-completion">
            <Award className="w-5 h-5" />
            <span>Completion</span>
            {getStepStatus("completed") === "locked" && <Lock className="w-4 h-4 ml-auto" />}
          </Link>
          
          <div className="border-t border-white/10 my-4"></div>
          
          <Link to="/dashboard" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-emerald-100 hover:bg-white/5 transition-colors" data-testid="nav-tools">
            <BarChart3 className="w-5 h-5" />
            <span>Tools & Resources</span>
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center space-x-3 mb-4 p-3 bg-white/5 rounded-lg">
            <div className="w-10 h-10 bg-emerald-700 rounded-full flex items-center justify-center">
              <span className="font-semibold">{user?.name?.charAt(0) || "U"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-emerald-200 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-emerald-200 hover:text-white hover:bg-white/10" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Back to Dashboard Button - Mobile Header */}
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/dashboard")} 
              className="text-neutral-600"
              data-testid="rapid-back-to-dashboard-mobile"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>

          {/* R.A.P.I.D.™ Framework Acronym Banner */}
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-center space-x-2 flex-wrap">
                <span className="font-bold text-purple-900 text-lg">R.A.P.I.D.™</span>
                <span className="text-purple-600">=</span>
                <div className="flex items-center space-x-2 flex-wrap justify-center">
                  <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Review</Badge>
                  <ChevronRight className="w-4 h-4 text-purple-400" />
                  <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Analyze</Badge>
                  <ChevronRight className="w-4 h-4 text-purple-400" />
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Plan</Badge>
                  <ChevronRight className="w-4 h-4 text-purple-400" />
                  <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">Implement</Badge>
                  <ChevronRight className="w-4 h-4 text-purple-400" />
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Develop</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Welcome Card */}
          <Card className="border-neutral-200 mb-8 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-900 to-emerald-700 p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-semibold">Welcome to R.A.P.I.D.™ Business Recalibration</h1>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate("/dashboard")} 
                  className="text-emerald-100 hover:text-white hover:bg-white/10 hidden lg:flex"
                  data-testid="rapid-back-to-dashboard-desktop"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Dashboard
                </Button>
              </div>
              <p className="text-emerald-100">
                This engagement is designed to remove internal constraints limiting leadership execution 
                and align your decisions with your next stage of growth.
              </p>
            </div>
            <CardContent className="p-6">
              {enrollment?.status === "intake_pending" ? (
                <Button onClick={() => navigate("/rapid/intake")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="begin-intake-btn">
                  Begin Intake
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-500 mb-1">Your Progress</p>
                    <p className="font-semibold text-neutral-900">{getProgressPercentage()}% Complete</p>
                  </div>
                  <Button variant="outline" onClick={() => {
                    const nextStep = getNextStep();
                    if (nextStep) navigate(nextStep);
                  }}>
                    Continue Journey
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* What This Is / Is Not */}
          <Card className="border-neutral-200 mb-8">
            <CardHeader>
              <CardTitle className="text-lg">What This Is / Is Not</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <p className="font-medium text-emerald-700">✔ This IS:</p>
                  <ul className="space-y-2 text-sm text-neutral-600">
                    <li className="flex items-start"><CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-emerald-500 flex-shrink-0" />Business consulting</li>
                    <li className="flex items-start"><CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-emerald-500 flex-shrink-0" />Leadership decision recalibration</li>
                    <li className="flex items-start"><CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-emerald-500 flex-shrink-0" />Execution alignment</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <p className="font-medium text-red-600">✘ This is NOT:</p>
                  <ul className="space-y-2 text-sm text-neutral-600">
                    <li className="flex items-start"><span className="w-4 h-4 mr-2 mt-0.5 text-red-400 flex-shrink-0">✗</span>Therapy</li>
                    <li className="flex items-start"><span className="w-4 h-4 mr-2 mt-0.5 text-red-400 flex-shrink-0">✗</span>Emotional processing</li>
                    <li className="flex items-start"><span className="w-4 h-4 mr-2 mt-0.5 text-red-400 flex-shrink-0">✗</span>Trauma work</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progress Tracker */}
          <Card className="border-neutral-200">
            <CardHeader>
              <CardTitle className="text-lg">Progress Tracker</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Progress value={getProgressPercentage()} className="h-2" />
              </div>
              <div className="flex justify-between">
                {RAPID_STEPS.map((step, index) => {
                  const status = getStepStatus(step.id);
                  return (
                    <div key={step.id} className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                        status === "completed" ? "bg-emerald-500 text-white" :
                        status === "current" || status === "in_progress" ? "bg-emerald-100 text-emerald-700 border-2 border-emerald-500" :
                        "bg-neutral-200 text-neutral-400"
                      }`}>
                        {status === "completed" ? <CheckCircle className="w-4 h-4" /> : index + 1}
                      </div>
                      <span className={`text-xs text-center ${status === "locked" ? "text-neutral-400" : "text-neutral-700"}`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );

  function getNextStep() {
    if (!enrollment) return "/rapid/intake";
    const status = enrollment.status;
    
    switch (status) {
      case "intake_pending": return "/rapid/intake";
      case "intake_complete": return "/rapid/session/1";
      case "session_1": return "/rapid/session/1";
      case "session_2": return "/rapid/session/2";
      case "session_3": return "/rapid/session/3";
      case "momentum": return "/rapid/momentum";
      case "completed": return "/rapid/completion";
      default: return "/rapid/intake";
    }
  }
};

export default RapidDashboard;
